import { useMemo, useState, type ElementType } from 'react';
import {
  Tag,
  Box,
  Button,
  ButtonGroup,
  Flex,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Skeleton,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiCalendar, FiFilter, FiSearch } from 'react-icons/fi';
import EmptyState from './EmptyState';
import { formatTransactionType, formatStatusLabel, getStatusColor, getTransactionMethodLabel } from '../utils/transactions';

type TransactionStatus = 'SUCCESS' | 'PAID' | 'DONE' | 'FAILED' | 'PENDING' | string;

type TransactionRecord = {
  id?: string;
  _id?: string;
  createdAt?: string;
  type?: string;
  method?: string;
  description?: string;
  amount?: number;
  status?: TransactionStatus;
  _transaction?: { status?: TransactionStatus } | null;
};

export interface TransactionsProps {
  isLoading?: boolean;
  error?: string;
  data: TransactionRecord[];
}

const formatCurrency = (value?: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format((value ?? 0) / 100);

const formatDateTime = (value?: string) =>
  value
    ? new Date(value).toLocaleString('pt-BR', {
        dateStyle: 'short',
        timeStyle: 'short',
      })
    : '—';

const Transactions = ({ isLoading = false, error, data }: TransactionsProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<'ALL' | 'CASH_IN' | 'CASH_OUT'>('ALL');
  const [statusFilter, setStatusFilter] = useState<'ALL' | TransactionStatus>('ALL');
  const tableStripeColor = useColorModeValue('rgba(15, 23, 42, 0.03)', 'rgba(148, 163, 184, 0.06)');
  const cardBorderColor = useColorModeValue('gray.200', 'gray.700');
  const cardBackground = useColorModeValue('white', 'gray.800');
  const headerBackground = useColorModeValue('gray.50', 'gray.900');
  const rowHoverBackground = useColorModeValue('gray.100', 'gray.700');

  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const matchesSearch = searchTerm
        ? item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.method?.toLowerCase().includes(searchTerm.toLowerCase())
        : true;
      const matchesType = typeFilter === 'ALL' ? true : item.type === typeFilter;
      const statusValue = item.status || item._transaction?.status;
      const matchesStatus = statusFilter === 'ALL' ? true : statusValue === statusFilter;
      return matchesSearch && matchesType && matchesStatus;
    });
  }, [data, searchTerm, typeFilter, statusFilter]);

  const totalCount = filteredData.length;
  const displayData = filteredData.slice(0, 10);

  if (!isLoading && !totalCount && !error) {
    return (
      <EmptyState
        title="Sem movimentações recentes"
        description="As transações aparecem aqui assim que novas entradas ou saídas são registradas."
      />
    );
  }

  return (
    <Stack spacing={6}>
      <Skeleton isLoaded={!isLoading} rounded="lg">
        <Box
          borderWidth="1px"
          borderColor={cardBorderColor}
          rounded="lg"
          p={{ base: 4, md: 6 }}
          bg={cardBackground}
          boxShadow="sm"
        >
          <Stack spacing={{ base: 4, md: 6 }}>
            <HStack spacing={4} flexWrap="wrap">
              <InputGroup maxW={{ base: '100%', md: '260px' }}>
                <InputLeftElement pointerEvents="none">
                  <Icon as={FiSearch as ElementType} color="text.500" />
                </InputLeftElement>
                <Input
                  placeholder="Buscar descrição ou método"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                />
              </InputGroup>
              <InputGroup maxW={{ base: '100%', md: '220px' }}>
                <InputLeftElement pointerEvents="none">
                  <Icon as={FiCalendar as ElementType} color="text.500" />
                </InputLeftElement>
                <Input placeholder="Período" readOnly cursor="pointer" />
              </InputGroup>
              <Select
                maxW={{ base: '100%', md: '200px' }}
                value={typeFilter}
                onChange={(event) => setTypeFilter(event.target.value as typeof typeFilter)}
              >
                <option value="ALL">Todos os tipos</option>
                <option value="CASH_IN">Entradas</option>
                <option value="CASH_OUT">Saídas</option>
              </Select>
              <Select
                maxW={{ base: '100%', md: '200px' }}
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value as typeof statusFilter)}
              >
                <option value="ALL">Todos os status</option>
                <option value="SUCCESS">Sucesso</option>
                <option value="PAID">Pago</option>
                <option value="DONE">Concluído</option>
                <option value="PENDING">Pendente</option>
                <option value="FAILED">Falha</option>
              </Select>
              <Button leftIcon={<Icon as={FiFilter as ElementType} />} variant="ghost">
                Filtrar
              </Button>
            </HStack>
            {error && (
              <Box
                borderWidth="1px"
                borderColor="danger.200"
                bg="danger.50"
                color="danger.700"
                rounded="lg"
                p={4}
              >
                {error}
              </Box>
            )}
            <Box overflowX="auto">
              <Table variant="striped" colorScheme="blackAlpha" size="md" sx={{ td: { verticalAlign: 'middle' } }}>
                <Thead bg={headerBackground} position="sticky" top={0} zIndex={1}>
                  <Tr>
                    <Th>Data</Th>
                    <Th>Tipo</Th>
                    <Th>Descrição</Th>
                    <Th isNumeric>Valor</Th>
                    <Th>Status</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {displayData.map((item) => {
                    const statusValue = item.status || item._transaction?.status || '';
                    const statusVariant = getStatusColor(statusValue) as 'success' | 'warning' | 'danger' | 'brand';
                    return (
                      <Tr
                        key={item._id || item.id}
                        _even={{ bg: tableStripeColor }}
                        _hover={{ bg: rowHoverBackground }}
                      >
                        <Td>{formatDateTime(item.createdAt)}</Td>
                        <Td>
                          <Text fontWeight="semibold">{formatTransactionType(item.type)}</Text>
                          <Text fontSize="sm" color="text.600">
                            {getTransactionMethodLabel(item.type || '', item.method || '')}
                          </Text>
                        </Td>
                        <Td>
                          <Text color="text.900">{item.description || '—'}</Text>
                        </Td>
                        <Td isNumeric>
                          <Text fontWeight="semibold" color="text.900">
                            {formatCurrency(item.amount)}
                          </Text>
                        </Td>
                        <Td>
                          <Tag variant={statusVariant} textTransform="capitalize">
                            {formatStatusLabel(statusValue)}
                          </Tag>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
            <Flex direction={{ base: 'column', md: 'row' }} align={{ base: 'flex-start', md: 'center' }} justify="space-between" gap={3}>
              <Text color="text.600" fontSize="sm">
                Mostrando {displayData.length} de {totalCount} transações
              </Text>
              <ButtonGroup size="sm" variant="ghost">
                <Button isDisabled>Anterior</Button>
                <Button isDisabled>Próxima</Button>
              </ButtonGroup>
            </Flex>
          </Stack>
        </Box>
      </Skeleton>
    </Stack>
  );
};

export default Transactions;
